/**
 * Enveloppe d'un bloc dans l'éditeur : header (badge type + actions), corps
 * (éditeur spécifique), toolbar latérale (déplacer, dupliquer, supprimer,
 * split/merge selon le type).
 */

import { useState } from 'react';
import {
  BookOpen,
  CheckCircle2,
  HelpCircle,
  Info,
  Lightbulb,
  Search,
  StickyNote,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { cn, Button } from '@jeraisonne/ui';
import { renderBlockEditor } from './BlockEditors';
import { VisualsEditorPanel } from './VisualsEditorPanel';
import { BlockVoiceTools } from './BlockVoiceTools';
import { GenerateClueHuntButton } from './clue-hunt/GenerateClueHuntButton';
import { QuestionScratchTools } from '@/features/scratch-editor';
import type { ExerciseBlock, ExerciseBlockType } from './types';

const TYPE_META: Record<
  ExerciseBlockType,
  { label: string; Icon: LucideIcon; iconColor: string; borderColor: string }
> = {
  statement: {
    label: 'Énoncé',
    Icon: BookOpen,
    iconColor: 'text-blue-700 bg-blue-50 ring-blue-200',
    borderColor: 'border-blue-200',
  },
  statement_question: {
    label: 'Question',
    Icon: HelpCircle,
    iconColor: 'text-indigo-700 bg-indigo-50 ring-indigo-200',
    borderColor: 'border-indigo-200',
  },
  correction: {
    label: 'Corrigé',
    Icon: CheckCircle2,
    iconColor: 'text-emerald-700 bg-emerald-50 ring-emerald-200',
    borderColor: 'border-emerald-200',
  },
  distractor: {
    label: 'Distracteur',
    Icon: XCircle,
    iconColor: 'text-amber-700 bg-amber-50 ring-amber-200',
    borderColor: 'border-amber-200',
  },
  explanation: {
    label: 'Explication',
    Icon: Info,
    iconColor: 'text-violet-700 bg-violet-50 ring-violet-200',
    borderColor: 'border-violet-200',
  },
  hint: {
    label: 'Indice',
    Icon: Lightbulb,
    iconColor: 'text-pink-700 bg-pink-50 ring-pink-200',
    borderColor: 'border-pink-200',
  },
  note: {
    label: 'Note tuteur',
    Icon: StickyNote,
    iconColor: 'text-slate-600 bg-slate-100 ring-slate-300',
    borderColor: 'border-slate-300',
  },
  clue_hunt: {
    label: 'Chasse aux indices',
    Icon: Search,
    iconColor: 'text-cyan-700 bg-cyan-50 ring-cyan-200',
    borderColor: 'border-cyan-200',
  },
};

export interface BlockShellProps {
  block: ExerciseBlock;
  questions: ExerciseBlock[];
  corrections: ExerciseBlock[];
  isFirst: boolean;
  isLast: boolean;
  isDisabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onInsertAfter: (type: ExerciseBlockType) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onPatchData: (data: Record<string, unknown>) => void;
  onPatchParent: (parent_id: string | null) => void;
  onIsolateVisual?: (visualId: string) => void;
  onSplitRange: (start: number, end: number) => void;
  onMergeWithPrev: () => void;
  canMergeWithPrev: boolean;
  onDoneEditing?: () => void;
  /** Active la dictée ciblée (question/corrigé) — requiert l'id de l'exercice + tous les blocs. */
  exerciseId?: string;
  allBlocks?: ExerciseBlock[];
  /**
   * Exercice PARAMÉTRIQUE d'un Blueprint (migration 0229) : le texte affiché
   * ici n'est que le rendu CANONIQUE — chaque tentative d'élève joue ses
   * propres valeurs. La chasse aux indices y est impossible (ses `source_text`
   * sont des sous-chaînes exactes du texte, qui change à chaque tirage).
   */
  isParametric?: boolean;
}

export function BlockShell(props: BlockShellProps) {
  const {
    block,
    questions,
    corrections,
    isFirst,
    isLast,
    isDisabled,
    onMoveUp,
    onMoveDown,
    onInsertAfter,
    onDelete,
    onDuplicate,
    onPatchData,
    onPatchParent,
    onIsolateVisual,
    onSplitRange,
    onMergeWithPrev,
    canMergeWithPrev,
    onDoneEditing,
    exerciseId,
    allBlocks,
  } = props;
  const meta = TYPE_META[block.block_type];
  const Icon = meta.Icon;
  const [showInsertMenu, setShowInsertMenu] = useState(false);

  const insertOptions: { type: ExerciseBlockType; label: string }[] =
    block.section === 'statement'
      ? [
          { type: 'statement_question', label: 'Question' },
          { type: 'statement', label: 'Énoncé / contexte' },
          { type: 'clue_hunt', label: 'Chasse aux indices' },
          { type: 'explanation', label: 'Explication' },
        ]
      : block.section === 'correction'
        ? [
            { type: 'correction', label: 'Étape de corrigé' },
            { type: 'distractor', label: 'Distracteur' },
            { type: 'hint', label: 'Indice' },
            { type: 'explanation', label: 'Explication' },
          ]
        : [{ type: 'note', label: 'Note' }];

  return (
    <article
      data-testid={`block-${block.id}`}
      data-mode="edit"
      className={cn(
        'group relative rounded-lg border-2 bg-white p-3 shadow-md ring-1 ring-slate-300 transition-all',
        meta.borderColor,
      )}
    >
      {/* Header */}
      <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex h-7 w-7 items-center justify-center rounded-md ring-1',
              meta.iconColor,
            )}
            title={meta.label}
            aria-label={meta.label}
          >
            <Icon className="h-4 w-4" aria-hidden />
          </span>
          <span className="text-xs italic text-slate-500">en édition</span>
        </div>
        <div className="flex items-center gap-1">
          {onDoneEditing ? (
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={onDoneEditing}
              disabled={isDisabled}
            >
              ✓ Terminé
            </Button>
          ) : null}
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst || isDisabled}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
            aria-label="Monter"
            title="Monter"
          >
            ▲
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast || isDisabled}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
            aria-label="Descendre"
            title="Descendre"
          >
            ▼
          </button>
          <button
            type="button"
            onClick={onDuplicate}
            disabled={isDisabled}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
            aria-label="Dupliquer"
            title="Dupliquer"
          >
            ⎘
          </button>
          {canMergeWithPrev ? (
            <button
              type="button"
              onClick={onMergeWithPrev}
              disabled={isDisabled}
              className="rounded p-1 text-slate-500 hover:bg-slate-100"
              aria-label="Fusionner avec le bloc précédent"
              title="Fusionner avec le bloc précédent (même type)"
            >
              ⤴
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDelete}
            disabled={isDisabled}
            className="rounded p-1 text-red-500 hover:bg-red-50 disabled:opacity-30"
            aria-label="Supprimer"
            title="Supprimer"
          >
            ✕
          </button>
        </div>
      </header>

      {/* Editor body */}
      <div className="space-y-2">
        {renderBlockEditor({
          block,
          questions,
          corrections,
          isDisabled,
          onPatchData,
          onPatchParent,
          onSplitRange,
          allBlocks,
        })}
      </div>

      {/* Dictée ciblée (question / corrigé / distracteur) — réponse, modification, figure */}
      {exerciseId &&
        allBlocks &&
        (block.block_type === 'statement_question' ||
          block.block_type === 'correction' ||
          block.block_type === 'distractor') && (
          <BlockVoiceTools exerciseId={exerciseId} block={block} blocks={allBlocks} />
        )}

      {/* Chasse aux indices (IA) — proposée UNIQUEMENT sur une question, et
          jamais sur un exercice paramétrique (cf. isParametric). */}
      {exerciseId &&
        allBlocks &&
        !props.isParametric &&
        block.block_type === 'statement_question' && (
          <GenerateClueHuntButton exerciseId={exerciseId} question={block} allBlocks={allBlocks} />
        )}

      {/* Feuille de brouillon de la question (lot 3) — jamais sur un exercice paramétrique. */}
      {exerciseId &&
        allBlocks &&
        !props.isParametric &&
        block.block_type === 'statement_question' && (
          <QuestionScratchTools exerciseId={exerciseId} question={block} allBlocks={allBlocks} />
        )}

      {/* Visuels attachés (si présents) — affichage + réordo + suppression */}
      <VisualsEditorPanel
        block={block}
        isDisabled={isDisabled}
        onPatchData={onPatchData}
        onIsolateVisual={onIsolateVisual}
      />

      {/* Insert-after row */}
      <footer className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
        <div className="ml-auto flex items-center gap-1">
          {!showInsertMenu ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowInsertMenu(true)}
              disabled={isDisabled}
            >
              + Insérer après
            </Button>
          ) : (
            <>
              {insertOptions.map((opt) => (
                <Button
                  key={opt.type}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onInsertAfter(opt.type);
                    setShowInsertMenu(false);
                  }}
                  disabled={isDisabled}
                >
                  {opt.label}
                </Button>
              ))}
              <button
                type="button"
                onClick={() => setShowInsertMenu(false)}
                className="px-1 text-xs text-slate-500"
              >
                ✕
              </button>
            </>
          )}
        </div>
      </footer>
    </article>
  );
}
